import { faCampground, faCrown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useStore } from "@nanostores/react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import MapGl, { type MapRef, Marker, NavigationControl, Popup } from "react-map-gl/maplibre";
import { switzerlandBounds, switzerlandBoundsWithPadding, switzerlandCenter } from "../services/coords";
import { $favoriteIds, isFavorite, toggleFavorite } from "../stores/favorites";
import type { CampsiteDataFragment, GetCampsitesQuery } from "../types/graphql/graphql";
import { CampsitePreviewCard } from "./CampsitePreviewCard";
import styles from "./CampsitesMap.module.css";
import { ResetMapControl } from "./ResetMapControl";

const mapStyles = {
  base: "https://vectortiles.geo.admin.ch/styles/ch.swisstopo.basemap.vt/style.json",
  light: "https://vectortiles.geo.admin.ch/styles/ch.swisstopo.lightbasemap.vt/style.json",
} as const;

type CampsitesMapProps = {
  campsitesData: GetCampsitesQuery | undefined;
  onExpand?: (ref: string) => void;
  onMove?: (moving: boolean) => void;
  onVisibleMarkersChanged?: (campsiteIds: number[]) => void;
  mapStyle?: keyof typeof mapStyles;
};

export const CampsitesMap = memo(function CampsitesMap({
  campsitesData,
  onExpand,
  onMove,
  onVisibleMarkersChanged,
  mapStyle = "light",
}: CampsitesMapProps) {
  const ref = useRef<MapRef>(null);
  const map = ref.current?.getMap();
  const zoomed = map && map?.getZoom() > 7;
  const [campsitePopup, setCampsitePopup] = useState<CampsiteDataFragment>();
  const favoriteIds = useStore($favoriteIds);
  const campsites = campsitesData?.campsites;
  const handleMoveStart = useCallback(() => {
    onMove?.(true);
  }, [onMove]);
  const handleMoveEnd = useCallback(() => {
    onMove?.(false);
    if (!onVisibleMarkersChanged || !map || !campsites) return;

    const bounds = map.getBounds();
    const visibleCampsiteIds = campsites
      ?.filter(
        ({ wgs84Coordinates }) =>
          wgs84Coordinates &&
          wgs84Coordinates.longitude >= bounds.getWest() &&
          wgs84Coordinates.longitude <= bounds.getEast() &&
          wgs84Coordinates.latitude >= bounds.getSouth() &&
          wgs84Coordinates.latitude <= bounds.getNorth(),
      )
      ?.map((campsite) => campsite.id);
    onVisibleMarkersChanged(visibleCampsiteIds);
  }, [campsites, onVisibleMarkersChanged, onMove, map]);

  const handleResetMap = useCallback(() => {
    map?.fitBounds(switzerlandBounds, { padding: 20 });
  }, [map]);

  useEffect(() => {
    // const map = ref.current?.getMap();
    if (campsitePopup?.wgs84Coordinates) {
      const { latitude, longitude } = campsitePopup.wgs84Coordinates;
      if (latitude && longitude)
        map?.flyTo({
          center: [longitude, latitude],
          zoom: 10,
          essential: true,
        });
    } else {
    }
  }, [map, campsitePopup]);

  // useEffect(() => {
  //   setTimeout(
  //     () =>
  //       ref.current?.fitBounds(switzerlandBounds, {
  //         padding: 20,
  //       }),
  //     100,
  //   );
  // }, []);

  const markers = useMemo(() => {
    return campsites?.map((campsite) => {
      // biome-ignore lint/complexity/noUselessFragments: TODO
      if (!campsite.wgs84Coordinates) return <></>;

      return (
        <Marker
          key={campsite.id}
          className={styles.marker}
          onClick={(e) => {
            e.originalEvent.stopPropagation();
            setCampsitePopup(campsite);
          }}
          {...campsite.wgs84Coordinates}
        >
          <FontAwesomeIcon
            color={campsite?.premium ? "var(--color-gold)" : "var(--bs-primary)"}
            className={styles.iconShadow}
            size={zoomed ? "2x" : "1x"}
            icon={campsite?.premium ? faCrown : faCampground}
          />
        </Marker>
      );
    });
  }, [campsites, zoomed]);

  return (
    <MapGl
      ref={ref}
      initialViewState={switzerlandCenter}
      maxBounds={switzerlandBoundsWithPadding}
      renderWorldCopies={false}
      attributionControl={false}
      minZoom={2}
      style={{ maxHeight: "40dvh", width: "100%", aspectRatio: "1/2" }}
      onMoveStart={handleMoveStart}
      onMoveEnd={handleMoveEnd}
      onLoad={handleMoveEnd}
      mapStyle={mapStyles[mapStyle]}
      scrollZoom={false}
      touchZoom={true}
      cooperativeGestures={true}
    >
      <NavigationControl showCompass={false} />
      <ResetMapControl onClick={handleResetMap} />

      {/* <Source id="switzerland-mask" type="geojson" data={chMask}>
        <Layer
          id="switzerland-mask-layer"
          type="fill"
          source="switzerland-mask"
          paint={{
              "fill-color": "#ffffff",
              "fill-opacity": 0.5,
            }}
        />
      </Source> */}
      {markers}
      {campsitePopup?.wgs84Coordinates && campsitesData?.campsites?.[0] && (
        <Popup
          className={styles.popup}
          onClose={() => setCampsitePopup(undefined)}
          maxWidth="30rem"
          closeButton={false}
          offset={15}
          {...campsitePopup.wgs84Coordinates}
        >
          <CampsitePreviewCard
            onToggleFavorite={toggleFavorite}
            isFavorite={isFavorite(campsitePopup.id, favoriteIds)}
            onExpand={onExpand}
            campsite={campsitePopup}
          />
        </Popup>
      )}
    </MapGl>
  );
});
